import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import './scss/user-side-panel.scss';

import { ReactComponent as DeleteIcon } from '../../assets/icons/delete-icon.svg';
// import { ReactComponent as NavigateArrow } from '../../assets/icons/device-management/navigate-back.svg';
import { ReactComponent as DotCircle } from '../../assets/icons/dot-circle.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';
import { RoleListInterFace } from '../../interface/role-management.interface';
import { UserInterFace, UserParameters } from '../../interface/user-management.interface';
import AddUser from '../../pages/user-management/component/AddUser';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import { fetchAllRolesWithOutPage } from '../../redux/slice/RoleManagementSlice';
import { fetchAllUsers, fetchUserActivityLogs } from '../../redux/slice/UserManagementSlice';
import { changeDateFormat } from '../../shared-functions/DataTime';
import {
  emailValidation,
  phoneNumberValidation,
  stringValidation,
} from '../../shared-functions/Validation';
import userApi from '../../utils/api/users';
import BatteryLoader from '../battery-loader/BatteryLoader';
import MyButton from '../button/Button';
import CustomInput from '../custom-input/CustomInput';
import CustomPhoneNumber from '../custom-phone-number-input/CustomPhoneNumberInput';
import DeleteWarning from '../delete-warning/DeleteWarning';
import ModalBox from '../modal-box/ModalBox';
import SelectFiled from '../select-field/SelectField';
import SuccessNotification from '../success-notification/SuccessNotification';

interface UserDetailsSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
  isEditMode?: boolean;
  onEditModeChange?: (state: boolean) => void;
  handleRefresh?: () => void;
}

interface UserEditParameters extends UserParameters {
  phoneNumber: string;
  countryCode: string;
}

/**
 * User details side panel component
 * @param {UserDetailsSidePanelProperties} properties - Properties for the side panel
 * @returns {React.ReactElement} - The rendered side panel
 */
function UserSidePanel(properties: UserDetailsSidePanelProperties) {
  const { t } = useTranslation(['userManagement', 'tableSidepanel']);
  const dispatch = useAppDispatch();
  const { onEditModeChange, fetchIdFrom, id, handleRefresh } = properties;
  const [searchParameters, setSearchParameters] = useSearchParams();
  const {
    users,
    userActiveLogs,
    loading: loggingLoading,
    page,
    limit,
  } = useAppSelector((state) => state.users);
  const { roleList } = useAppSelector((state) => state.roles);

  const [userData, setUserData] = useState<UserInterFace | null>(null);
  const [currentId, setCurrentId] = useState<string | null>(null);

  // Modals state
  const [modalView, setModalView] = useState({
    status: false,
    title: '',
    content: <div />,
    header: false,
    rightCorner: '',
    notification: false,
  });

  const [isEdit, setIsEdit] = useState(false);
  const [editData, setEditData] = useState<any>({});
  const [loading, setLoading] = useState(false);

  /**
   * Handle cancel/close side panel
   */
  const handleClosePanel = () => {
    const parameters = new URLSearchParams(searchParameters);
    parameters.delete('userId');
    parameters.delete('edit');
    setSearchParameters(parameters);
  };

  // Initial load
  useEffect(() => {
    let userId = id;
    if (fetchIdFrom === 'queryParams') {
      userId = searchParameters.get('userId') || undefined;
    }
    setCurrentId(userId || null);

    if (userId && userId !== 'add-user') {
      const found = users.find((u: UserInterFace) => u.id === userId || u._id === userId);
      if (found) {
        setUserData(found);
        // Fetch logs
        dispatch(fetchUserActivityLogs(userId));

        // Sync edit mode with query parameters
        const isEditRequested = searchParameters.get('edit') === 'true';
        if (isEditRequested) {
          setEditData({
            name: found.name || '',
            email: found.email || '',
            phoneNumber: found.phoneNumber || '',
            countryCode: found.countryCode || '',
            role: found.role?._id || '',
            roleName: found.roleName || found.role?.name || '',
            status: found.status ? 'Active' : 'Inactive',
          });
          setIsEdit(true);
          if (onEditModeChange) onEditModeChange(true);
        } else {
          setIsEdit(false);
          if (onEditModeChange) onEditModeChange(false);
        }
      }
    } else {
      setUserData(null);
      setIsEdit(false);
    }
  }, [fetchIdFrom, id, searchParameters, users]);

  // Fetch roles
  useEffect(() => {
    if (roleList.length === 0) {
      dispatch(fetchAllRolesWithOutPage());
    }
  }, []);

  /**
   * Toggle Edit Mode
   */
  const toggleEditMode = () => {
    if (isEdit) {
      // Cancelling edit mode
      setIsEdit(false);
      setEditData({});
      if (onEditModeChange) onEditModeChange(false);
    } else {
      // Entering edit mode
      setEditData({
        name: userData?.name || '',
        email: userData?.email || '',
        phoneNumber: userData?.phoneNumber || '',
        countryCode: userData?.countryCode || '',
        role: userData?.role?._id || '',
        roleName: userData?.roleName || userData?.role?.name || '',
        status: userData?.status ? 'Active' : 'Inactive',
      });
      setIsEdit(true);
      if (onEditModeChange) onEditModeChange(true);
    }
  };

  /**
   * Handle Input Change
   */
  const handleInputChange = (name: string, value: string | number | (string | number)[]) => {
    setEditData((previous: any) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Handle Edit Submit
   */
  const handleEditSubmit = async () => {
    if (!userData) return;

    if (
      editData.role === '' ||
      !stringValidation(1, 100, editData.name) ||
      editData.email === '' ||
      editData.phoneNumber === '' ||
      editData.countryCode === '' ||
      !emailValidation(editData.email) ||
      !phoneNumberValidation(editData.phoneNumber)
    ) {
      dispatch(
        setNotificationDetails({
          notificationDetails: {
            message: t('pleaseFillRequiredFields') || 'Please fill all required fields',
            statusCode: 400,
          },
          type: 'error',
        }),
      );
      return;
    }

    setLoading(true);
    const request: UserEditParameters = {
      name: editData.name,
      role: editData.role,
      email: editData.email.toLowerCase(),
      phoneNumber: editData.phoneNumber,
      countryCode: editData.countryCode,
    };

    try {
      await userApi.editUser(userData.id, request);
      setLoading(false);
      setModalView({
        status: true,
        title: '',
        header: false,
        content: (
          <SuccessNotification
            title={t('userUpdatedSuccessfully')}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setModalView((previous) => ({ ...previous, status: false }));
              handleClosePanel();
              if (handleRefresh) {
                handleRefresh();
              }
            }}
          />
        ),
        rightCorner: '',
        notification: true,
      });
      setIsEdit(false);
      if (onEditModeChange) onEditModeChange(false);
    } catch (error: any) {
      setLoading(false);
      dispatch(
        setNotificationDetails({
          notificationDetails: {
            message: error?.response?.data?.message,
            statusCode: error?.response?.data?.code,
          },
          type: 'error',
        }),
      );
    }
  };

  /**
   * The delete the user confirm function
   */
  const confirmUserDelete = (userId: string) => {
    userApi
      .deleteUser(userId)
      .then(() => {
        dispatch(fetchAllUsers({ page, limit }));
        setModalView({
          status: true,
          title: '',
          header: false,
          content: (
            <SuccessNotification
              title={t('userDeleted')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModalView((previous) => ({ ...previous, status: false }));
                handleClosePanel();
              }}
            />
          ),
          rightCorner: '',
          notification: true,
        });
      })
      .catch((error) => {
        setModalView((previous) => ({ ...previous, status: false }));
        dispatch(
          setNotificationDetails({
            notificationDetails: {
              message: error?.response?.data?.message,
              statusCode: error?.response?.data?.code,
            },
            type: 'error',
          }),
        );
      });
  };

  /**
   * Handle Delete Click
   */
  const handleDeleteClick = () => {
    if (!userData) return;
    setModalView({
      status: true,
      title: '',
      header: false,
      content: (
        <DeleteWarning
          selectedRowId={userData._id}
          title={t('deleteUserTitle')}
          description={t('deleteUserDescription')}
          cancelButtonOnClick={() => setModalView((previous) => ({ ...previous, status: false }))}
          submitSvgIcon={<DeleteIcon />}
          submitButtonOnClick={(userId) => userId && confirmUserDelete(userId)}
        />
      ),
      rightCorner: '',
      notification: true,
    });
  };

  if (currentId === 'add-user') {
    return (
      <div className="user-details-side-panel">
        <AddUser handleCancel={handleClosePanel} />
      </div>
    );
  }

  if (!userData) return <div className="no-data">{t('noDataFound')}</div>;

  return (
    <div className="user-details-side-panel">
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setModalView((previous) => ({ ...previous, status: false }))}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />
      <div className="panel-header">
        <p className="header-label">{t('userDetails')}</p>
        {!isEdit && !userData.deletedAt && (
          <div className="action-buttons">
            <EditIcon onClick={toggleEditMode} className="edit icon" />
            <DeleteIcon onClick={handleDeleteClick} className="delete icon" />
          </div>
        )}
      </div>

      <div className="scroll-wrapper">
        <div className="user-details-group">
          <div className="profile-card">
            {isEdit ? (
              // Edit Mode Fields
              <div className="edit-fields-container">
                <div className="profile-info">
                  <div className="name">{userData?.name || '-'}</div>
                  <div className="role">
                    {userData?.roleName || userData?.role?.name || t('manager')}
                  </div>
                  <div className="divider" />
                  <div className="detail-row">
                    <div className="label">{t('createdAt')}:</div>
                    <div className="value bold">
                      {userData?.createdAt ? changeDateFormat(userData?.createdAt) : '-'}
                    </div>
                  </div>
                  <div className="spacer-20" />

                  <div className="info-row-input">
                    <CustomInput
                      type="text"
                      name="name"
                      placeholder={t('userName')}
                      value={editData.name}
                      onChangeValue={handleInputChange}
                      inputLabel={t('userName')}
                    />
                  </div>

                  <div className="info-row-input">
                    <span className="role-label">{t('role')}</span>
                    <SelectFiled
                      name="role"
                      inputLabel=""
                      selectedValue={editData.role}
                      placeholder={t('selectRole')}
                      onChangeValue={handleInputChange}
                      options={roleList.map((role: RoleListInterFace) => ({
                        label: role.name,
                        value: role._id,
                      }))}
                    />
                  </div>

                  <div className="info-row-input">
                    <CustomInput
                      type="email"
                      name="email"
                      placeholder={t('email')}
                      value={editData.email}
                      onChangeValue={handleInputChange}
                      inputLabel={t('email')}
                    />
                  </div>

                  <div className="info-row-input">
                    <CustomPhoneNumber
                      name="phoneNumber"
                      placeholder={t('phoneNumber')}
                      value={editData.phoneNumber}
                      countryCode={editData.countryCode}
                      onChangeValue={handleInputChange}
                      inputLabel={t('phoneNumber')}
                    />
                  </div>

                  <div className="info-row-input">
                    <p className="label status-label-text"> {t('status')}:</p>
                    <div className="tag-success-label">
                      <DotCircle /> <span className="status-text">{t('active')}</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // View Mode Layout
              <div className="profile-info">
                <div className="name">{userData?.name || '-'}</div>
                <div className="role">
                  {userData?.roleName || userData?.role?.name || t('manager')}
                </div>

                <div className="divider" />

                <div className="detail-row">
                  <div className="label">{t('createdAt')}:</div>
                  <div className="value bold">
                    {userData?.createdAt ? changeDateFormat(userData?.createdAt) : '-'}
                  </div>
                </div>

                <div className="detail-row">
                  <div className="label">{t('contact')}:</div>
                  <div className="value bold">{userData?.email || '-'}</div>
                </div>

                <div className="detail-row">
                  <div className="label">{t('status')}:</div>
                  {!userData.deletedAt ? (
                    <div className="tag-success-label">
                      <DotCircle /> <span>{t('active')}</span>
                    </div>
                  ) : (
                    <div className="tag-danger-label">
                      <DotCircle /> <span>{t('inactive')}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {!isEdit && (
            <div className="section activity-log-section">
              <div className="activity-header">
                <h4>{t('activityLog')}</h4>
              </div>

              <div className="timeline-list">
                {loggingLoading && (
                  <div className="not-found loader-container">
                    <BatteryLoader />
                  </div>
                )}

                {!loggingLoading &&
                  userActiveLogs.length > 0 &&
                  userActiveLogs.map((activityLog, index) => (
                    <React.Fragment key={activityLog.date || index}>
                      {activityLog?.logs?.map((log, logIndex) => (
                        <div className="timeline-item" key={log._id || logIndex}>
                          <div className="timeline-marker">
                            <div className="dot" />
                            <div className="line" />
                          </div>
                          <div className="timeline-content">
                            <div className="log-message">{log.title || t('actionPerformed')}</div>
                          </div>
                          <div className="timeline-time">
                            <div>
                              {activityLog.date} | {log.time}
                            </div>
                          </div>
                        </div>
                      ))}
                    </React.Fragment>
                  ))}

                {!loggingLoading && userActiveLogs.length === 0 && (
                  <div className="no-activity-logs">{t('noActivityLogs')}</div>
                )}
              </div>
            </div>
          )}
        </div>

        {isEdit && (
          <div className="footer-actions">
            <MyButton label={t('cancel')} buttonType="outline" onClickFunc={toggleEditMode} />
            <MyButton
              label={t('save')}
              buttonType="submit"
              onClickFunc={handleEditSubmit}
              disableButton={loading}
              loading={loading}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default UserSidePanel;
